"use client";

import { useRef, useEffect, type ReactNode } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import axios from "axios";
import { logger } from "@/helpers/tslog";
import { getToken } from "@/utils/localstorage";
import { useContentSlideStore } from "@/state/contentSlideStore";
import { buildNewPrecedentTimePeriodOpeningMarkdown } from "../../utils/timePeriodBenchmarkOpeningMarkdown";


const INIT_SENTINEL = "__INIT_NewPrecedentTimePeriodBenchmarkStep__";

const PLACEHOLDER_REPLY_TEXT = "Thanks — we received your message. (Placeholder reply.)";

/** Slightly faster than 8ms × full length for long opening markdown. */
const STREAM_STEP_CHARS = 2;
const STREAM_STEP_DELAY_MS = 5;

type CurrentUserResponse = {
  id: string;
  email: string;
  fullName: string;
  conversationId: string | null;
};

async function fetchUserFirstName(): Promise<string> {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) return "there";
  try {
    const token = await getToken();
    const { data } = await axios.get<CurrentUserResponse>(`${apiUrl}/user/current-user`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    const first = data?.fullName?.trim().split(/\s+/)[0];
    return first || "there";
  } catch (e) {
    logger.warn("ContentSlideAlphaCreateRuntime: failed to load current user", e);
    return "there";
  }
}

const MyModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator {
    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as { text: string }).text;
    }
    const isInitialization = userInput === INIT_SENTINEL;

    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch {
      // ignore
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    let fullText: string;
    if (isInitialization) {
      const userFirstName = await fetchUserFirstName();
      const { primaryCompany } = useContentSlideStore.getState();
      const companyName = primaryCompany?.companyName?.trim() || "your company";
      fullText = buildNewPrecedentTimePeriodOpeningMarkdown(
        userFirstName,
        companyName
      );
    } else {
      fullText = PLACEHOLDER_REPLY_TEXT;
    }

    let end = 0;
    let firstChunk = true;
    while (end < fullText.length) {
      end = Math.min(end + STREAM_STEP_CHARS, fullText.length);
      if (firstChunk) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch {
          // ignore
        }
        firstChunk = false;
      }
      yield {
        content: [{ type: "text", text: fullText.slice(0, end) }],
      };
      await delay(STREAM_STEP_DELAY_MS);
    }

    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch {
      // ignore
    }
  },
};

export function NewPrecedentSlideRuntime({ children }: { children: ReactNode }) {
  const runtime = useLocalRuntime(MyModelAdapter, {
    initialMessages: [],
  });

  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;

    try {
      localStorage.removeItem("messages");
    } catch (e) {
      logger.warn("Failed to clear messages cache", e);
    }

    try {
      const thread = (runtime as { thread?: { append: (m: unknown) => void } })?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: INIT_SENTINEL }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch {
      // ignore
    }
  }, [runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
